import { CommonModule } from '@angular/common';
import { Component, computed, inject, OnInit, signal, ViewChild } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTabsModule } from '@angular/material/tabs';
import { AddeditLabdatacaptureComponent } from './addedit-labdatacapture/addedit-labdatacapture.component';
import { LabdataService } from '../../services/labdata.service';
import { GetLabDataCaptureBySiteIdResponse, LabDataCaptureSites } from '../../interfaces/siteType';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatMenuModule } from "@angular/material/menu";
import { LabDataPayload } from '../../interfaces/labDataModel';
import { AlertDialogComponent } from '../shared/components/alert-dialog/alert-dialog.component';
import { ConfirmationDialogComponent } from '../shared/components/confirmation-dialog/confirmation-dialog.component';
import { RolePermissionsService } from '../../services/role-permissions.service';
import { LoadScheduleControlFlags, ScreenName } from '../../interfaces/securityPermissions';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { formatDateOnly } from '../shared/commonutils/helper';
import { RoleService } from '../../services/role.service';
import { LookUpService } from '../../services/lookupservice';

@Component({
  selector: 'app-labdatacapture',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatCardModule,
    MatTabsModule,
    MatMenuModule,
    MatTableModule,
    MatDatepickerModule
  ],
  templateUrl: './labdatacapture.component.html',
  styleUrl: './labdatacapture.component.scss'
})
export class LabdatacaptureComponent implements OnInit {
  permissions = inject(RolePermissionsService);
  private dialog = inject(MatDialog);
  private service = inject(LabdataService);
  private rolesService= inject(RoleService);
  controlPermissions: LoadScheduleControlFlags = new LoadScheduleControlFlags();
  range: FormGroup = new FormGroup({
    start: new FormControl<Date | null>(null),
    end: new FormControl<Date | null>(null),
  });
  displayedColumns: string[] = [ 'actions', 'siteName', 'tankName', 'dateCaptured', 'reportDate',
    'labDataName','labReportId','cod', 'total_solids', 'volatile_solids',
    'ammonia', 'sulfate', 'pH', 'tic', 'vfa', 'vfaByTIC', 'vfA_Acetic', 'vfA_ropionic',
    'vfA_Butyric', 'vfA_Valeric', 'tkn', 'dissolved_sulfur',
    'chlorides','totalOrganicCarbon','phosphorus', 'potassium', 'oilandGrease',
    'iron', 'vfaIsovaleric', 'vfaSuccinic', 'vfaCaproic','vfaLactic','createdBy'
  ];
  dataSource = new MatTableDataSource<GetLabDataCaptureBySiteIdResponse>();
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  // State management using signals
  ldcSites = signal<LabDataCaptureSites[]>([]);
  selectedSiteIdControl = new FormControl<number | null>(null);
  selectedSiteId = this.selectedSiteIdControl.valueChanges.pipe().subscribe((id) => {
    this.selectedSiteIdValue.set(id);
    this.savedPayload.set([]);
  });
  selectedSiteIdValue = signal<number | null>(null);

  savedPayload = signal<LabDataPayload[]>([]);
  savedPayloadJson = computed(() => JSON.stringify(this.savedPayload(), null, 2));

  ngOnInit(): void {
    const today = new Date();
    const firstDayOfCurrentWeek = this.getFirstDayOfWeek(today);
    firstDayOfCurrentWeek.setDate(today.getDate() - 7);
    today.setDate(today.getDate() + 2);

    const initialRange = new FormGroup({
      start: new FormControl<Date | null>(firstDayOfCurrentWeek),
      end: new FormControl<Date | null>(today)
    });
    this.range = initialRange;
    this.loadStreams();
  }

  onSiteChange() {
    this.loadCapturedData();
  }

  onSearch(){
    this.loadCapturedData();
  }

  loadCapturedData() {
    this.range.controls['start'].markAsDirty();
    this.range.controls['start'].markAsTouched();
    this.range.controls['end'].markAsDirty();
    this.range.controls['end'].markAsTouched();
    this.range.controls['start'].updateValueAndValidity();
    this.range.controls['end'].updateValueAndValidity();
    this.range.markAllAsTouched();
    this.range.updateValueAndValidity();
    
    if (this.range.invalid) {
      this.dialog.open(AlertDialogComponent, {
        data: {
          title: 'Invalid Date Range',
          message: 'Please select a valid date range.'
        },
        disableClose: true,
        panelClass: 'delete-confirmation-dialog-nowrap'
      });
      return;
    }
    if (this.range.invalid || !this.range.value.start || !this.range.value.end) {
      this.dialog.open(AlertDialogComponent, {
        data: {
          title: 'Invalid Date Range',
          message: 'Please select a valid date range.'
        },
        disableClose: true,
        panelClass: 'delete-confirmation-dialog-nowrap'
      });
      return;
    }
    
    const fromDate = this.range.value.start ? formatDateOnly(this.range.value.start) : '';
    const toDate = this.range.value.end ? formatDateOnly(this.range.value.end) : '';
    const siteId = this.selectedSiteIdValue();
    this.service.getLabDataCaptureData(siteId == null ? '0' : siteId.toString(), fromDate, toDate).subscribe((response) => {
      console.log('Captured Data Response:', response);
      this.dataSource.data = response;
        
      this.dataSource.sort = this.sort;
      this.dataSource.paginator = this.paginator;

    });
  }

  loadStreams(){
    let roles = this.rolesService.getRolesAsString();
    this.permissions.getRolePermissionsNew(roles, ScreenName.LabDataCapture, 0).subscribe({
      next: (perms: LoadScheduleControlFlags) => {
        this.controlPermissions = perms;
      }
    })
    this.service.getLabdataSites().subscribe((response) => {
      this.ldcSites.set(response);
    });
  }

  onAddOrEdit(row: GetLabDataCaptureBySiteIdResponse | null, task: string) {
    if(task === 'Add' && this.selectedSiteIdValue() === null){
      this.dialog.open(AlertDialogComponent, {
        data: {
          title: 'Alert',
          message: 'Please select a site before adding a new Lab Data Capture entry.',
          buttonText: 'OK'
        },
        disableClose: true,
        panelClass: 'delete-confirmation-dialog-nowrap'
      }).afterClosed().subscribe(() => {});
      return;
    }
    const siteId = task === 'Edit' ? row?.siteId : this.selectedSiteIdValue();
    const siteName = this.ldcSites().find(s => s.id === siteId)?.siteName || 'N/A';
    const dialogRef = this.dialog.open(AddeditLabdatacaptureComponent, {
      data: {
        siteId: siteId,
        siteName: siteName,
        isEdit: task === 'Edit',
        dateCaptured: row ? row.dateCaptured?.toLocaleString("MM/dd/yyyy") : new Date(),
        reportDate: row ? row.reportDate?.toLocaleString("MM/dd/yyyy") : new Date(),
        userName: task === 'Edit' ? row?.createdByName : localStorage.getItem("loggedUserName") ?? '',
        id: row ? row.id : 0,
        canShowSaveButton: this.controlPermissions.addButton || this.controlPermissions.edit,
        editTankId: row ? row.tankId : null,
        editDigesterId: row ? row.digesterId : null,
        labDataId: row ? row.labId : null,
        labReportId: row ? row.labReportId : ''
      },
      width: '1050px',
      maxWidth: '1050px',
      disableClose: true,
      maxHeight: '90vh'
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.loadCapturedData();
      }
    });
  }
  
  onDelete(id: number) {
    this.dialog.open(ConfirmationDialogComponent, {
      panelClass: 'delete-confirmation-dialog',
      data: {
        title: 'Confirm Deletion',
        message: `Are you sure you want to delete this Lab Data Capture entry?`,
        confirmButtonText: 'Delete',
        cancelButtonText: 'Cancel'
      }
    }).afterClosed().subscribe((confirmed) => {
      if(confirmed){
        this.service.deleteLabDataCaptureData(id).subscribe((response) => {
          if (response) {
            this.loadCapturedData();
          }
        });
      }
    });
  }

  getFirstDayOfWeek(date: Date) {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day;
    d.setDate(diff);
    return d;
  }

  onKeydown(event: KeyboardEvent) {
    if(event.ctrlKey && event.metaKey){return;} 
    if(!/[0-9\/]/.test(event.key) && event.key.length === 1){
      event.preventDefault();
    }
    setTimeout(() => {
      this.range.updateValueAndValidity();
      this.range.markAllAsTouched();
    });
  }

  dateformat(event: Event){
    const input = event.target as HTMLInputElement;
    let value = input.value;

    if(!value) return;
    const parts = value.split('/');
    if(parts.length !== 3) return;

    let [month, day, year] = parts.map(part => part.padStart(2, '0'));
    if(month.length === 1)  month = '0' + month;
    if(day.length === 1)  day = '0' + day;
    if(year.length === 2) year = '20' + year;
    input.value = `${month}/${day}/${year}`;
  }
}